import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { QuickGroupCard } from "./QuickGroupCard";
import { QuickGroupQuestionsModal, quickGroupPreviewTarget } from "./QuickGroupQuestionsModal";
import { ListingHeader } from "./ListingTitle";
import { sortBundles, SORT_OPTIONS } from "./assignmentDraft";

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// Two equal columns that grow to fill the available width (Figma 3805-26076,
// both cards are flex-[1_0_0]). minmax(0, 1fr) — not 1fr — so a long standard
// title can't force the column past its share and overflow the row.
const Grid = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

// Text-only "Show All / Show Fewer" toggle (Figma 4010-31367 / 4010-31410).
const ShowToggle = styled.button`
  align-self: center;
  background: transparent;
  border: none;
  color: #524fc9;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px;
`;

// Collapsed by default we show at most this many cards; the rest are behind
// the "Show All" toggle.
const COLLAPSED_LIMIT = 4;

// "Quick Groups" list section for the Groups page (Figma 3805-26076). Renders a
// card per quick group; hidden entirely when the class has none.
export const QuickGroupsSection = ({
  quickGroups,
  className,
  style,
  classroomId,
  onChanged,
  // The standards flow labels the sequence option "Standards"; every other
  // listing keeps the lesson wording.
  sortOptions = SORT_OPTIONS,
}) => {
  const [previewTarget, setPreviewTarget] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [sortBy, setSortBy] = useState("recent");

  // Same Sort By options as the Lessons listing; quick groups have no lesson
  // ordinals, so "Lesson Sequence" orders them by standard code.
  const sorted = useMemo(() => sortBundles(quickGroups || [], sortBy), [quickGroups, sortBy]);

  if (sorted.length === 0) return null;

  const overLimit = sorted.length > COLLAPSED_LIMIT;
  const visible = expanded ? sorted : sorted.slice(0, COLLAPSED_LIMIT);

  return (
    <Section className={className} style={style}>
      <ListingHeader
        title="Quick Groups"
        sortValue={sortBy}
        sortOptions={sortOptions}
        onSortChange={setSortBy}
      />
      <Grid>
        {visible.map((qg) => (
          <QuickGroupCard
            key={qg.id || qg.code}
            quickGroup={qg}
            classroomId={classroomId}
            onDeleted={onChanged}
            onDuplicated={onChanged}
            onPreview={() => setPreviewTarget(quickGroupPreviewTarget(qg))}
          />
        ))}
      </Grid>
      {overLimit && (
        <ShowToggle type="button" onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Show Fewer Groups" : `Show All ${sorted.length} Groups`}
        </ShowToggle>
      )}
      <QuickGroupQuestionsModal
        target={previewTarget}
        classroomId={classroomId}
        onClose={() => setPreviewTarget(null)}
        onChanged={onChanged}
      />
    </Section>
  );
};
